/**
 * FilterChips — the All / Blocks / Follows / Boosts / Skips bar
 * across the top of PendingSuggestionsPage. Each chip carries a
 * count badge; selecting one filters the page.
 *
 * The "active" state uses the verb's primary background so the
 * filter visually matches the rows below it. "All" uses the slate
 * neutral.
 */
import React from 'react';
import {Box, Stack} from '@mui/material';
import FilterListOutlined from '@mui/icons-material/FilterListOutlined';
import {SuggestionVerb} from '../../types/generated/graphql';
import {VERB_TONES, verbTokenForVerb} from './EvidenceSheet/tones';

export type SuggestionFilter = 'all' | SuggestionVerb;

export interface FilterChipsProps {
  value: SuggestionFilter;
  onChange: (next: SuggestionFilter) => void;
  counts: {
    all: number;
    block: number;
    follow: number;
    boost: number;
    skip: number;
  };
}

interface ChipDef {
  key: SuggestionFilter;
  label: string;
  tone: 'slate' | 'block' | 'follow' | 'boost' | 'skip';
  count: number;
}

function ChipButton({
  chip,
  active,
  onClick,
}: {
  chip: ChipDef;
  active: boolean;
  onClick: () => void;
}): React.JSX.Element {
  const t = chip.tone === 'slate' ? null : VERB_TONES[chip.tone];
  return (
    <Box
      component="button"
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        borderRadius: '999px',
        border: active
          ? `1px solid ${t?.pillBorder ?? '#0F172A'}`
          : t
            ? `1px solid ${t.pillBorder}`
            : '1px solid #E2E8F0',
        backgroundColor: active
          ? t?.pillBg ?? '#0F172A'
          : '#FFFFFF',
        color: active
          ? t?.pillText ?? '#FFFFFF'
          : t?.pillText ?? '#475569',
        px: 1.5,
        py: 0.5,
        fontWeight: 500,
        fontSize: '12px',
        textTransform: 'none',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: active
            ? t?.pillBg ?? '#0F172A'
            : '#F1F5F9',
        },
      }}
    >
      {chip.label}
      <Box
        component="span"
        sx={{
          borderRadius: '4px',
          px: '6px',
          minWidth: '18px',
          textAlign: 'center',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          fontSize: '10px',
          lineHeight: '16px',
          // Stronger contrast: inactive badge sits on a dark surface so
          // we need a noticeably lighter bg + bright text for the count
          // to be legible. Active badge inverts to the verb tone.
          backgroundColor: active ? 'rgba(255, 255, 255, 0.2)' : '#E2E8F0',
          color: active ? '#FFFFFF' : '#475569',
        }}
      >
        {chip.count}
      </Box>
    </Box>
  );
}

export function FilterChips({value, onChange, counts}: FilterChipsProps): React.JSX.Element {
  const chips: ChipDef[] = [
    {key: 'all', label: 'All', tone: 'slate', count: counts.all},
    {key: SuggestionVerb.Block, label: 'Blocks', tone: 'block', count: counts.block},
    {key: SuggestionVerb.Follow, label: 'Follows', tone: 'follow', count: counts.follow},
    {key: SuggestionVerb.Boost, label: 'Boosts', tone: 'boost', count: counts.boost},
    {key: SuggestionVerb.Skip, label: 'Skips', tone: 'skip', count: counts.skip},
  ];
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <FilterListOutlined sx={{fontSize: 14, color: '#64748B'}} />
      {chips.map(chip => (
        <ChipButton
          key={chip.key}
          chip={chip}
          active={value === chip.key}
          onClick={() => onChange(chip.key)}
        />
      ))}
    </Stack>
  );
}

export function tokenForFilter(
  filter: SuggestionFilter,
): SuggestionVerb | undefined {
  if (filter === 'all') return undefined;
  return filter;
}

export function countByVerb(suggestions: Array<{verb: SuggestionVerb}>) {
  const counts = {all: suggestions.length, block: 0, follow: 0, boost: 0, skip: 0};
  for (const s of suggestions) {
    const k = verbTokenForVerb(s.verb);
    counts[k] += 1;
  }
  return counts;
}
